import { useEffect, useMemo, useRef } from "react";
import {
  Background,
  BaseEdge,
  EdgeProps,
  Handle,
  Position,
  getBezierPath,
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
  type Edge,
  type Node,
  type Viewport,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { Check, LocateFixed, ZoomIn, ZoomOut } from "lucide-react";

import {
  flowEdges,
  flowNodes,
  aiTradingFlowNodes,
  aiTradingFlowEdges,
  aiTradingStep4Overrides,
} from "../../demoData";
import type { FlowNode, FlowEdge } from "../../types";

// Nodes that have no incoming target handle (source/root nodes)
const NO_TARGET_HANDLE_IDS = new Set(["kline", "timer"]);

// Nodes that have no outgoing source handle (terminal/leaf nodes)
const NO_SOURCE_HANDLE_IDS = new Set([
  "buy",
  "sell",
  "log",
  "longopen_push",
  "shortopen_push",
  "flatlong_push",
  "flatshort_push",
  "noaction_push",
  "openai1",
  "openai2",
  "openai3",
  "telegram",
  "stoploss",
]);

function DemoNode({ data }: { data: FlowNode & { sourceHandleIds?: string[] } }) {
  const Icon = data.icon;
  const noTarget = NO_TARGET_HANDLE_IDS.has(data.id);
  const noSource = NO_SOURCE_HANDLE_IDS.has(data.id);
  const sourceHandleIds = data.sourceHandleIds ?? (noSource ? [] : ["default"]);
  return (
    <div className="flex flex-col items-center justify-center anim-zoom-in" style={{ width: "120px" }}>
      <div
        className={`w-16 h-16 rounded-2xl border-2 flex items-center justify-center relative shadow-sm ${data.borderColor} ${data.bg}`}
      >
        {!noTarget && (
          <Handle
            id="target"
            type="target"
            position={Position.Left}
            isConnectable={false}
            style={{ opacity: 0, pointerEvents: "none" }}
          />
        )}
        {sourceHandleIds.map((hid) => (
          <Handle
            key={hid}
            id={hid}
            type="source"
            position={Position.Right}
            isConnectable={false}
            style={{ opacity: 0, pointerEvents: "none" }}
          />
        ))}
        <Icon size={24} strokeWidth={2} className={data.color} />
        {!noTarget && (
          <div className="absolute -left-1.5 top-1/2 -translate-y-1/2 w-2.5 h-2.5 bg-gray-400 rounded-full border border-white" />
        )}
        {!noSource && (
          <div className="absolute -right-1.5 top-1/2 -translate-y-1/2 w-2.5 h-2.5 bg-gray-400 rounded-full border border-white" />
        )}
        <div className="absolute bottom-1 right-1 w-3 h-3 bg-white rounded-full flex items-center justify-center shadow-sm">
          <Check size={8} className="text-green-500" />
        </div>
      </div>
      <div className="mt-2 text-center">
        <div className="text-[13px] font-semibold text-gray-800">{data.label}</div>
        <div className="text-[11px] text-gray-400 mt-0.5">{data.sub}</div>
      </div>
    </div>
  );
}

function GreenBezierEdge(props: EdgeProps) {
  const [edgePath] = getBezierPath({
    sourceX: props.sourceX,
    sourceY: props.sourceY,
    sourcePosition: props.sourcePosition,
    targetX: props.targetX,
    targetY: props.targetY,
    targetPosition: props.targetPosition,
  });
  const endX = props.targetX;
  const endY = props.targetY;
  return (
    <g className="anim-draw-line">
      <BaseEdge path={edgePath} style={{ stroke: "#4ade80", strokeWidth: 2, strokeLinecap: "round" }} />
      <circle cx={endX - 2} cy={endY} r={3} fill="#4ade80" />
    </g>
  );
}

// Strategy-specific node selection
function getStrategyNodes(strategyType: "dual_ma" | "ai_trading") {
  if (strategyType === "ai_trading") {
    return { nodes: aiTradingFlowNodes, edges: aiTradingFlowEdges };
  }
  return { nodes: flowNodes, edges: flowEdges };
}

function FlowCanvasInner({ currentFlowStep, strategyType }: { currentFlowStep: number; strategyType: "dual_ma" | "ai_trading" }) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const viewportRef = useRef<Viewport>({ x: 50, y: 50, zoom: 1 });

  const { fitView, setViewport, zoomIn, zoomOut } = useReactFlow();

  const { nodes: strategyNodes, edges: strategyEdges }: { nodes: FlowNode[]; edges: FlowEdge[] } = useMemo(
    () => getStrategyNodes(strategyType),
    [strategyType]
  );

  const visibleNodes = useMemo(() => strategyNodes.filter((n) => n.step <= currentFlowStep), [strategyNodes, currentFlowStep]);
  const visibleEdges = useMemo(
    () => strategyEdges.filter(
      (e) => e.step <= currentFlowStep && (e.maxStep == null || currentFlowStep < e.maxStep)
    ),
    [strategyEdges, currentFlowStep]
  );

  // Build a map: nodeId -> list of edge IDs that originate from it
  const sourceHandleMap = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const e of visibleEdges) {
      if (!map.has(e.from)) map.set(e.from, []);
      map.get(e.from)!.push(e.id);
    }
    return map;
  }, [visibleEdges]);

  // For ai_trading step 4, some nodes shift to new coordinates
  const isStep4Override = strategyType === "ai_trading" && currentFlowStep >= 4;

  const nodes = useMemo<Node[]>(
    () =>
      visibleNodes.map((n) => {
        const override = isStep4Override ? aiTradingStep4Overrides[n.id] : undefined;
        return {
          id: n.id,
          type: "demo",
          position: override ?? { x: n.x, y: n.y },
          data: { ...n, sourceHandleIds: sourceHandleMap.get(n.id) ?? [] },
          draggable: false,
          selectable: false,
        };
      }),
    [visibleNodes, sourceHandleMap, isStep4Override]
  );

  const edges = useMemo<Edge[]>(
    () =>
      visibleEdges.map((e) => ({
        id: e.id,
        source: e.from,
        target: e.to,
        sourceHandle: e.id,
        targetHandle: "target",
        type: "greenBezier",
        selectable: false,
      })),
    [visibleEdges]
  );

  const maxStep = useMemo(() => Math.max(...strategyNodes.map((n) => n.step)), [strategyNodes]);

  const doFitView = () => {
    fitView({ padding: 0.2, duration: 800 });
  };

  useEffect(() => {
    if (currentFlowStep <= 0) return;

    if (currentFlowStep === maxStep) {
      requestAnimationFrame(() => doFitView());
      return;
    }

    const el = wrapRef.current;
    if (!el) return;

    const stepNodes = strategyNodes.filter((n) => n.step === currentFlowStep);
    if (stepNodes.length === 0) return;

    const minX = Math.min(...stepNodes.map((n) => n.x));
    const maxX = Math.max(...stepNodes.map((n) => n.x)) + 120;
    const minY = Math.min(...stepNodes.map((n) => n.y));
    const maxY = Math.max(...stepNodes.map((n) => n.y)) + 64;
    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;

    const { clientWidth, clientHeight } = el;
    const z = viewportRef.current.zoom;
    requestAnimationFrame(() => {
      setViewport(
        {
          x: clientWidth / 2 - centerX * z,
          y: clientHeight / 2 - centerY * z,
          zoom: z,
        },
        { duration: 800 }
      );
    });
  }, [currentFlowStep, maxStep]);

  return (
    <div ref={wrapRef} style={{ position: "absolute", inset: 0 }}>
      <div className="absolute bottom-6 right-6 flex flex-col gap-2 z-20 pointer-events-auto">
        <button
          onClick={() => zoomIn({ duration: 300 })}
          className="bg-white border border-gray-200 p-2.5 rounded-xl shadow-sm hover:bg-gray-50 text-gray-600 transition-colors"
          title="Zoom In"
        >
          <ZoomIn size={18} />
        </button>
        <button
          onClick={() => zoomOut({ duration: 300 })}
          className="bg-white border border-gray-200 p-2.5 rounded-xl shadow-sm hover:bg-gray-50 text-gray-600 transition-colors"
          title="Zoom Out"
        >
          <ZoomOut size={18} />
        </button>
        <button
          onClick={doFitView}
          className="bg-white border border-gray-200 p-2.5 rounded-xl shadow-sm hover:bg-gray-50 text-gray-600 transition-colors"
          title="Fit View"
        >
          <LocateFixed size={18} />
        </button>
      </div>

      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={{ demo: DemoNode }}
        edgeTypes={{ greenBezier: GreenBezierEdge }}
        defaultViewport={{ x: 50, y: 50, zoom: 1 }}
        minZoom={0.15}
        maxZoom={3}
        zoomOnDoubleClick={false}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        proOptions={{ hideAttribution: true }}
        onMove={(_, viewport) => {
          viewportRef.current = viewport;
        }}
      >
        <Background color="#d1d5db" gap={24} size={1.5} />
      </ReactFlow>
    </div>
  );
}

export function FlowCanvas({ currentFlowStep, strategyType = "dual_ma" }: { currentFlowStep: number; strategyType?: "dual_ma" | "ai_trading" }) {
  return (
    <main className="flex-1 h-full bg-[#fdfdfd] relative overflow-hidden shadow-inner">
      <ReactFlowProvider>
        <FlowCanvasInner currentFlowStep={currentFlowStep} strategyType={strategyType} />
      </ReactFlowProvider>
    </main>
  );
}
